import SessionAvatar from 'components/common/SessionAvatar';
import OngoingSessionHeaderContainer from 'components/ongoingParking/OngoingSessionHeaderContainer';
import {
  HeaderContainer,
  OngoingParkingHeaderWrapper,
} from 'components/ongoingParking/styles';
import useDurationFromDate from 'hooks/useDurationFromDate';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useOngoingSessionsSheet from 'hooks/useOngoingSessionsSheet';
import useVehicle from 'hooks/useVehicle';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';
import { useChargingStore } from 'stores/chargingStore';
import { useParkingStore } from 'stores/parkingStore';
import { ParkingSession } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import { getTimeLeft } from 'utils/timeUtils';
import {
  CollapseButton,
  ExtendButton,
  HeaderTexts,
  ListButton,
  OngoingParkingTimeline,
  OpenSheetButton,
  TerminateButton,
} from './OngoingParkingComponents';
import { useUiStore } from 'stores/uiStore';

type ParkingHeaderProps = {
  session: ParkingSession;
  isManual?: boolean;
  showExtendButton: boolean;
};

const OngoingParkingHeader: React.FC<ParkingHeaderProps> = ({
  session,
  isManual = true,
  showExtendButton,
}) => {
  const endTime = useMemo(
    () =>
      session?.predefinedEndDate
        ? new Date(session?.predefinedEndDate as string)
        : null,
    [session?.predefinedEndDate]
  );
  const startTime = new Date(session?.startDate as Date);
  const licensePlate = session?.accessDevice?.value || '';
  const { t } = useTranslation();
  const isExpanded =
    useParkingStore((state) => state.selectedOngoingSession) === 'PARKING';
  const ongoingChargingSessionId = useChargingStore(
    (state) => state.ongoingChargingSession
  );
  const [timeNow, setTimeNow] = useState(new Date());
  const [isMultipleSession, setIsMultipleSession] = useState(false);
  const { parkingZoneResult, allParkingSessions } = useOngoingParkingSession();
  const { label } = useVehicle(licensePlate);
  const { collapsedSheetContainerStyle, expandedSheetContainerStyle } =
    useOngoingSessionsSheet();
  const isANPR = session.type === 'ANPR';

  const { overdue, timeLeft } = getTimeLeft(endTime, timeNow);
  const timeLeftTrans = t(overdue ? 'time.overdue' : 'time.remaining', {
    timeLeft,
  });

  const duration = useDurationFromDate(startTime, 30000, [
    'days',
    'hours',
    'minutes',
  ]);

  useEffect(() => {
    const handler = setInterval(() => {
      setTimeNow(new Date());
    }, 1000);

    return () => {
      clearInterval(handler);
    };
  }, [setTimeNow]);

  useEffect(() => {
    setIsMultipleSession(
      !!ongoingChargingSessionId ||
        !!(allParkingSessions && allParkingSessions.length > 1)
    );
  }, [ongoingChargingSessionId, allParkingSessions]);

  const openSheet = () => {
    useParkingStore.getState().setSelectedOngoingSession('PARKING');
  };

  const commercialZoneName = `${
    parkingZoneResult?.data?.parkZone?.commercialZoneView?.zoneCode ?? ''
  } ${parkingZoneResult?.data?.parkZone?.commercialZoneView?.name ?? ''}`;
  return (
    <HeaderContainer>
      <OngoingSessionHeaderContainer
        type="PARKING"
        testID="ongoing-parking-container">
        <OngoingParkingHeaderWrapper mr={20} testID="vehicle-avatar-container">
          <SessionAvatar session={session} />
        </OngoingParkingHeaderWrapper>
        <OngoingParkingHeaderWrapper mr={20} grow>
          <HeaderTexts
            vehicleLabel={label}
            isOverdue={overdue}
            timeLeft={timeLeftTrans}
            isManual={isManual}
            parkZoneName={commercialZoneName}
            isANPR={isANPR}
            elapsedTime={duration}
            extended={isExpanded}
          />
        </OngoingParkingHeaderWrapper>
        <Animated.View
          style={expandedSheetContainerStyle}
          testID="ongoing-parking-header-expanded">
          <>
            {isMultipleSession && (
              <OngoingParkingHeaderWrapper mr={isWeb ? 0 : 20}>
                <ListButton />
              </OngoingParkingHeaderWrapper>
            )}
            {!isWeb && (
              <OngoingParkingHeaderWrapper>
                <CollapseButton />
              </OngoingParkingHeaderWrapper>
            )}
          </>
        </Animated.View>
        <Animated.View
          style={collapsedSheetContainerStyle}
          testID="ongoing-parking-manual-header-collapsed">
          {isManual && (
            <>
              <OngoingParkingHeaderWrapper mr={20}>
                <TerminateButton
                  disabled={overdue}
                  onPress={() =>
                    useUiStore.getState().setSheetModal('stopOngoingParking')
                  }
                />
              </OngoingParkingHeaderWrapper>
              <OngoingParkingHeaderWrapper>
                {showExtendButton && (
                  <ExtendButton
                    onPress={() =>
                      useUiStore
                        .getState()
                        .setSheetModal('extendOngoingParking')
                    }
                  />
                )}
              </OngoingParkingHeaderWrapper>
            </>
          )}
          {isANPR && (
            <OngoingParkingHeaderWrapper>
              <OpenSheetButton onPress={openSheet} />
            </OngoingParkingHeaderWrapper>
          )}
        </Animated.View>
      </OngoingSessionHeaderContainer>
      <OngoingParkingTimeline
        startTime={startTime}
        endTime={endTime}
        isExpanded={isExpanded}
        currentTime={timeNow}
      />
    </HeaderContainer>
  );
};

export default OngoingParkingHeader;
